import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { LegalFeeStage } from '@/types/legalFees';
import { computePaidStatus } from '@/pages/fixedAssets/components/fixed-asset-detail/legal-fees/legalFees.utils';
import type { EditItemFormData } from '@/pages/fixedAssets/components/fixed-asset-detail/legal-fees/legalFees.schema';

// ---------------------------------------------------------------------------
// Payload types
// ---------------------------------------------------------------------------

export interface LegalFeeItemConfig {
  title: string;
  currency: string;
  amount: number;
  description?: string | null;
  sort_order?: number;
}

export interface LegalFeeStageConfig {
  name: string;
  sort_order: number;
  items: LegalFeeItemConfig[];
}

export interface RecordItemPaymentPayload {
  itemId: string;
  fixedAssetId: string;
  accountId: string;
  amount: number;
  paymentDate: string;
  notes?: string | null;
  paymentCurrency: string;
  stageName: string;
  itemTitle: string;
  propertyName: string;
}

export interface RecordStagePaymentPayload {
  stageId: string;
  fixedAssetId: string;
  accountId: string;
  totalAmount: number;
  paymentDate: string;
  notes?: string | null;
  paymentCurrency: string;
  stageName: string;
  propertyName: string;
}

// ---------------------------------------------------------------------------
// useLegalFeeStages
// Fetches stages with nested items and payments; computes paid_status per item;
// orders stages by sort_order ascending.
// Validates: Requirements 3.7, 4.1–4.4
// ---------------------------------------------------------------------------

export function useLegalFeeStages(fixedAssetId: string | undefined) {
  return useQuery({
    queryKey: ['legal-fee-stages', fixedAssetId],
    queryFn: async (): Promise<LegalFeeStage[]> => {
      const { data: stages, error } = await supabase
        .from('legal_fee_stages')
        .select(`
          *,
          items:legal_fee_items(
            *,
            payments:legal_fee_item_payments(*)
          )
        `)
        .eq('fixed_asset_id', fixedAssetId!)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      return (stages ?? []).map((stage) => ({
        ...stage,
        items: (stage.items ?? [])
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((item) => {
            const totalPaid = (item.payments ?? []).reduce(
              (sum, p) => sum + Number(p.amount),
              0,
            );
            return {
              ...item,
              total_paid: totalPaid,
              paid_status: computePaidStatus(Number(item.amount), totalPaid),
              payments: item.payments ?? [],
            };
          }),
      }));
    },
    enabled: !!fixedAssetId,
  });
}

// ---------------------------------------------------------------------------
// useCreateLegalFeeStages
// Calls create_legal_fee_stages RPC with the user-configured stages JSONB.
// Validates: Requirements 4.2, 9.3
// ---------------------------------------------------------------------------

export function useCreateLegalFeeStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      fixedAssetId,
      stages,
    }: {
      fixedAssetId: string;
      stages: LegalFeeStageConfig[];
    }) => {
      const { error } = await (supabase.rpc as any)('create_legal_fee_stages', {
        p_fixed_asset_id: fixedAssetId,
        p_stages: stages,
      });
      if (error) throw error;
    },
    onSuccess: (_, { fixedAssetId }) => {
      qc.invalidateQueries({ queryKey: ['legal-fee-stages', fixedAssetId] });
    },
  });
}

// ---------------------------------------------------------------------------
// useRecordItemPayment
// Calls create_legal_fee_item_payment RPC.
// Validates: Requirements 5.3–5.7
// ---------------------------------------------------------------------------

export function useRecordItemPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payload: RecordItemPaymentPayload) => {
      const { error } = await (supabase.rpc as any)('create_legal_fee_item_payment', {
        p_user_id: user!.id,
        p_legal_fee_item_id: payload.itemId,
        p_account_id: payload.accountId,
        p_amount: payload.amount,
        p_payment_date: payload.paymentDate,
        p_notes: payload.notes ?? null,
        p_payment_currency: payload.paymentCurrency,
        p_fixed_asset_id: payload.fixedAssetId,
        p_stage_name: payload.stageName,
        p_item_title: payload.itemTitle,
        p_property_name: payload.propertyName,
      });
      if (error) throw error;
    },
    onSuccess: (_, payload) => {
      qc.invalidateQueries({ queryKey: ['legal-fee-stages', payload.fixedAssetId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useRecordStagePayment
// Calls create_legal_fee_stage_payment RPC.
// Validates: Requirements 6.3–6.6
// ---------------------------------------------------------------------------

export function useRecordStagePayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payload: RecordStagePaymentPayload) => {
      const { error } = await (supabase.rpc as any)('create_legal_fee_stage_payment', {
        p_user_id: user!.id,
        p_stage_id: payload.stageId,
        p_account_id: payload.accountId,
        p_total_amount: payload.totalAmount,
        p_payment_date: payload.paymentDate,
        p_notes: payload.notes ?? null,
        p_fixed_asset_id: payload.fixedAssetId,
        p_stage_name: payload.stageName,
        p_property_name: payload.propertyName,
        p_payment_currency: payload.paymentCurrency,
      });
      if (error) throw error;
    },
    onSuccess: (_, payload) => {
      qc.invalidateQueries({ queryKey: ['legal-fee-stages', payload.fixedAssetId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateLegalFeeItem
// Direct update on legal_fee_items table; no optimistic update.
// Validates: Requirements 10.2–10.5
// ---------------------------------------------------------------------------

export function useUpdateLegalFeeItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      fixedAssetId,
      ...fields
    }: EditItemFormData & { itemId: string; fixedAssetId: string }) => {
      const { error } = await supabase
        .from('legal_fee_items')
        .update({ ...fields, updated_at: new Date().toISOString() })
        .eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: (_, { fixedAssetId }) => {
      qc.invalidateQueries({ queryKey: ['legal-fee-stages', fixedAssetId] });
    },
  });
}
